import { useEffect } from 'react';

/**
 * Custom hook to suppress ResizeObserver errors
 * This is a common issue with Ant Design components and browser resize detection
 */
export const useResizeObserverErrorSuppression = () => {
  useEffect(() => {
    let originalConsoleError: typeof console.error;

    const setupErrorSuppression = () => {
      // Store original console.error
      originalConsoleError = console.error;

      // Override console.error to filter ResizeObserver errors
      console.error = (...args: any[]) => {
        const errorMessage = args[0];

        // Check if this is a ResizeObserver error
        if (
          typeof errorMessage === 'string' &&
          (errorMessage.includes('ResizeObserver loop completed') ||
            errorMessage.includes('ResizeObserver loop limit exceeded') ||
            errorMessage.includes('ResizeObserver'))
        ) {
          return; // Suppress the error
        }

        // Call original console.error for other errors
        originalConsoleError.apply(console, args);
      };

      // Handle window errors
      const handleError = (e: ErrorEvent) => {
        if (
          e.message.includes('ResizeObserver loop completed') ||
          e.message.includes('ResizeObserver loop limit exceeded') ||
          e.message.includes('ResizeObserver')
        ) {
          e.stopImmediatePropagation();
          e.preventDefault();
          return false;
        }
      };

      // Handle unhandled promise rejections
      const handleRejection = (e: PromiseRejectionEvent) => {
        if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver')) {
          e.preventDefault();
        }
      };

      window.addEventListener('error', handleError, { capture: true });
      window.addEventListener('unhandledrejection', handleRejection);

      return () => {
        // Cleanup: restore original console.error and remove listeners
        console.error = originalConsoleError;
        window.removeEventListener('error', handleError, { capture: true });
        window.removeEventListener('unhandledrejection', handleRejection);
      };
    };

    const cleanup = setupErrorSuppression();

    return cleanup;
  }, []);
};
